/**
 * Full-screen media viewer opened from a `Gallery`/`MediaTile`. Focus-trapped
 * (built on the same `trapFocus` as `Modal`/`Drawer`), closes on Escape or
 * scrim click, restores focus on unmount. Optional prev/next controls also
 * respond to ArrowLeft/ArrowRight. Token-driven, reduced-motion honored.
 */
import type { JSX } from 'solid-js';
import { For, onCleanup, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { trapFocus } from '../focus/trap-focus.ts';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';
import { Icon } from './Icon.tsx';
import type { IconName } from './icons.ts';

export type LightboxItemKind = 'image' | 'video';

const STYLE_ID = 'f4-lightbox';
const CSS = `
.f4-lightbox-scrim {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-lightbox, 500);
}
.f4-lightbox-scrim__backdrop {
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  background: var(--color-scrim);
  cursor: pointer;
  animation: f4-fade-in var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-lightbox {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  width: 100%;
  height: 100%;
  padding: var(--space-6);
  animation: f4-scale-in var(--duration-base, 240ms) var(--ease-spring, ease);
}
.f4-lightbox:focus-visible { outline: none; }
.f4-lightbox__media { max-width: 100%; max-height: 100%; object-fit: contain; }
.f4-lightbox__close {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--space-8);
  height: var(--space-8);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-circle);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  cursor: pointer;
}
.f4-lightbox__nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--space-10);
  height: var(--space-10);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-circle);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  cursor: pointer;
}
.f4-lightbox__actions {
  position: absolute;
  bottom: var(--space-4);
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  gap: var(--space-2);
  padding: var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill, var(--radius-lg));
  background: var(--color-surface-overlay);
}
.f4-lightbox__action {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text);
  font: inherit;
  cursor: pointer;
}
.f4-lightbox__action:hover:not(:disabled) { background: var(--color-surface-raised); }
.f4-lightbox__action:disabled { opacity: 0.5; cursor: default; }
@media (prefers-reduced-motion: reduce) {
  .f4-lightbox-scrim__backdrop, .f4-lightbox { animation: none; }
}
`;

/** A real caption track for a video item — no fields, no `<track>`. */
export interface LightboxCaptions {
  /** WebVTT track URL. */
  readonly src: string;
  /** BCP-47 language tag of the captions, e.g. `en`. */
  readonly srclang: string;
  /** Human-readable track name shown in the player's caption menu. */
  readonly label: string;
}

export interface LightboxItem {
  readonly src: string;
  readonly alt: string;
  readonly kind?: LightboxItemKind;
  /** Caption track for a video item; omit it and no `<track>` is rendered. */
  readonly captions?: LightboxCaptions;
}

/**
 * A generic action button in the viewer's toolbar — the caller supplies the
 * label + glyph + handler, so domain actions (download, set-as-avatar) stay in
 * the app and the primitive stays domain-free (OCP: add an action, no fork).
 */
interface LightboxActionBase {
  /** Visible + accessible label. */
  readonly label: string;
  /** Invoked on activation. */
  readonly onSelect: () => void;
  /** Disable while an action is in flight (e.g. a save round-trip). */
  readonly disabled?: boolean;
}

/**
 * An action's glyph comes from the shared icon set (`iconName`, preferred — the
 * geometry then lives in one place and is painted correctly) or, for the
 * pre-existing callers, a raw SVG path (`icon`). Exactly one, never both:
 * the union makes "an action with no glyph" and "an action with two" both
 * unrepresentable.
 */
export type LightboxAction = LightboxActionBase &
  (
    | { readonly iconName: IconName; readonly icon?: undefined }
    | { readonly icon: string; readonly iconName?: undefined }
  );

interface LightboxBaseProps {
  /** Whether the viewer is shown. */
  readonly open: boolean;
  /** The media currently on display. `undefined` while `open` is false. */
  readonly item?: LightboxItem;
  /** Requested close (Escape, scrim click, close button). */
  readonly onClose: () => void;
  /** Accessible label for the close button (required). */
  readonly closeLabel: string;
  /** Toolbar actions (download, set-as-avatar, …) rendered inside the focus trap. */
  readonly actions?: readonly LightboxAction[];
  readonly class?: string;
}

/**
 * A nav control and its accessible label are one unit: provide the callback
 * and you MUST provide a non-empty label (it becomes the button's
 * `aria-label`), or omit both. Encoding the pair as a discriminated union
 * makes an empty `aria-label` unrepresentable.
 */
type LightboxPrevProps =
  | { readonly onPrev?: undefined; readonly prevLabel?: undefined }
  | { readonly onPrev: () => void; readonly prevLabel: string };
type LightboxNextProps =
  | { readonly onNext?: undefined; readonly nextLabel?: undefined }
  | { readonly onNext: () => void; readonly nextLabel: string };

export type LightboxProps = LightboxBaseProps & LightboxPrevProps & LightboxNextProps;

export function Lightbox(props: LightboxProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);

  const onArrowKeys = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowLeft' && props.onPrev) {
      event.preventDefault();
      props.onPrev();
    } else if (event.key === 'ArrowRight' && props.onNext) {
      event.preventDefault();
      props.onNext();
    }
  };

  // `attach` runs inside the `<Show>` branch's reactive owner, so cleanup
  // registered here fires whenever the dialog CLOSES (the branch is disposed),
  // not only when Lightbox unmounts. This restores focus to the trigger on
  // every close and builds a fresh trap on every open — no stale trap state
  // survives a close/reopen cycle.
  const attach = (node: HTMLDivElement): void => {
    const dispose = trapFocus(node, props.onClose);
    node.addEventListener('keydown', onArrowKeys);
    onCleanup(() => {
      dispose();
      node.removeEventListener('keydown', onArrowKeys);
    });
  };

  return (
    <Show when={props.open && props.item}>
      {(item) => (
        <Portal>
          <div class="f4-lightbox-scrim">
            <button
              type="button"
              class="f4-lightbox-scrim__backdrop"
              aria-label={props.closeLabel}
              onClick={() => props.onClose()}
            />
            <div
              ref={attach}
              role="dialog"
              aria-modal="true"
              aria-label={item().alt}
              tabindex="-1"
              class={cx('f4-lightbox', props.class)}
            >
              <button
                type="button"
                class="f4-lightbox__close f4-focus-ring"
                aria-label={props.closeLabel}
                onClick={() => props.onClose()}
              >
                <Icon name="close" size="md" />
              </button>
              <Show when={props.onPrev}>
                <button
                  type="button"
                  class="f4-lightbox__nav f4-focus-ring"
                  aria-label={props.prevLabel}
                  onClick={() => props.onPrev?.()}
                >
                  <Icon name="chevron-left" size="md" />
                </button>
              </Show>
              <Show
                when={item().kind === 'video'}
                fallback={<img class="f4-lightbox__media" src={item().src} alt={item().alt} />}
              >
                <video class="f4-lightbox__media" src={item().src} controls>
                  {/* One static <track> (required by a11y/useMediaCaption). It
                      stays inert — no src/srclang/label — until the item
                      supplies real captions, at which point it renders them. */}
                  <track
                    kind="captions"
                    src={item().captions?.src}
                    srclang={item().captions?.srclang}
                    label={item().captions?.label}
                  />
                </video>
              </Show>
              <Show when={props.onNext}>
                <button
                  type="button"
                  class="f4-lightbox__nav f4-focus-ring"
                  aria-label={props.nextLabel}
                  onClick={() => props.onNext?.()}
                >
                  <Icon name="chevron-right" size="md" />
                </button>
              </Show>
              <Show when={props.actions && props.actions.length > 0}>
                <div class="f4-lightbox__actions" role="toolbar" aria-label={item().alt}>
                  <For each={props.actions}>
                    {(action) => (
                      <button
                        type="button"
                        class="f4-lightbox__action f4-focus-ring"
                        aria-label={action.label}
                        disabled={action.disabled}
                        onClick={() => action.onSelect()}
                      >
                        <Icon name={action.iconName} path={action.icon} size="sm" />
                        <span>{action.label}</span>
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </div>
        </Portal>
      )}
    </Show>
  );
}
